import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft, ShieldCheck, CheckCircle2, Clock, BadgeCheck, ChevronRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { IdentityVerificationForm } from "../components/IdentityVerificationForm";
import { profileKey, captureProfileGuard } from "../lib/profileSession";
import ProfessionalVerificationSection from "../components/ProfessionalVerificationSection";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/beui/SurfaceCard";
import "./WorkerVerification.mobile.css";

export default function WorkerVerification() {
  const { t } = useI18n();
  const { user, loading, refresh } = useAuth();
  const [, navigate] = useLocation();
  const [loadedState, setState] = useState<(Awaited<ReturnType<typeof api.workerKyc>> & { ownerUserId: number; ownerProfile: string; identityApproved: boolean }) | null>(null);
  const loadVersion = useRef(0);
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;
  const [loadError, setLoadError] = useState(false);
  async function load() {
    const ownerUserId = user?.id;
    if (!ownerUserId) return;
    const version = ++loadVersion.current;
    const assertCurrent = captureProfileGuard();
    setState(null);
    setLoadError(false);
    try {
      const [result, legacy] = await Promise.all([api.workerKyc(), api.myVerification()]);
      assertCurrent();
      if (currentUserId.current !== ownerUserId || loadVersion.current !== version) return;
      // Match the server's retained ID compatibility without treating company or credential approval as identity.
      const identityApproved = result.verification.status === "approved" || result.workerVerified
        || (legacy.verified && legacy.verifiedKind === "id");
      setState({ ...result, ownerUserId, ownerProfile: profileKey(user), identityApproved });
      setLoadError(false);
    } catch {
      if (currentUserId.current !== ownerUserId || loadVersion.current !== version) return;
      setState(null);
      setLoadError(true);
    }
  }
  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (user) void load();
    return () => { loadVersion.current++; };
  }, [user?.id, user?.activeContext, user?.workerVerified]);

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  const state = loadedState?.ownerProfile === profileKey(user) ? loadedState : null;
  const status = state?.verification?.status || "none";
  const identityApproved = !!state?.identityApproved;
  return (
    <div className="verification-page container-x py-6 max-w-5xl">
      <div className="verification-heading mb-5 flex flex-wrap items-center justify-between gap-3">
        <nav className="verification-breadcrumb" aria-label={t("settings.title")}>
          <div className="verification-ancestors">
            <Link href="/dashboard">{t("dashboard.accountMenu.account")}</Link>
            <ChevronRight aria-hidden="true" />
            <Link href="/settings">{t("settings.title")}</Link>
            <ChevronRight aria-hidden="true" />
          </div>
          <h1 aria-current="page" className="text-2xl font-extrabold text-navy flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-brand-red" />Verification</h1>
        </nav>
        <Link href="/settings" className="verification-desktop-back inline-flex items-center gap-2 text-sm font-semibold text-mut hover:text-navy">
          <ArrowLeft className="w-4 h-4" /> Back to settings
        </Link>
      </div>

      {loadError && <div className="card p-5 text-sm text-brand-red" role="alert">
        Could not load your verification status. <button type="button" onClick={() => void load()} className="underline font-semibold">Retry</button>
      </div>}
      {!state && !loadError && <p className="text-sm text-mut" role="status">Loading verification status…</p>}

      {state && <div className="verification-tracks">
        <a href="#identity-verification" className="card p-5"><ShieldCheck className="w-5 h-5 text-brand-red" aria-hidden="true" /><div><strong>Verify it’s you</strong><p className="text-sm text-mut mt-1">{identityApproved ? "Identity verified" : status === "pending" ? "Under review" : status === "rejected" ? "Changes needed" : "Government ID and supporting documents"}</p></div></a>
        <a href="#professional-verification" className="card p-5"><BadgeCheck className="w-5 h-5 text-navy" aria-hidden="true" /><div><strong>Verify your profession</strong><p className="text-sm text-mut mt-1">Optional · {identityApproved ? "Add your qualifications" : "Requires approved identity"}</p></div></a>
      </div>}
      <div className="verification-layout">
      {state && (
        <div id="identity-verification">
          {identityApproved ? (
            <Card className="verification-status flex-row items-center gap-4 p-5">
              <div className="verification-status-icon bg-emerald-50 text-emerald-700"><CheckCircle2 aria-hidden="true" /></div>
              <div>
                <h2 className="text-lg font-bold text-navy">Your identity is verified</h2>
              </div>
            </Card>
          ) : status === "pending" ? (
            <Card className="verification-status flex-row items-center gap-4 p-5">
              <div className="verification-status-icon bg-amber-50 text-amber-700"><Clock aria-hidden="true" /></div>
              <div>
                <h2 className="text-lg font-bold text-navy">Identity under review</h2>
                <p className="text-sm text-mut mt-1">Our team is reviewing your documents. You'll be notified once it's done.</p>
              </div>
            </Card>
          ) : (
            <IdentityVerificationForm key={profileKey(user)} verification={state.verification} onChange={verification => {
              setState(current => current?.ownerUserId === user.id ? { ...current, verification } : current);
              if (verification.status === "pending") void refresh();
            }} />
          )}

        </div>
      )}
      {state && <ProfessionalVerificationSection identityApproved={identityApproved} />}
      </div>
    </div>
  );
}
